import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { inr, inrShort } from "@/lib/format";
import { PageTitle } from "@/components/PageTitle";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Link } from "react-router-dom";
import {
  Users, ShoppingCart, Wallet, Scale, Receipt, UserCog, TrendingUp, ArrowRight,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const Stat = ({ icon: Icon, label, value, tone = "primary", testid }) => (
  <Card className="p-5 card-elevate border-border/70" data-testid={testid}>
    <div className="flex items-start justify-between">
      <div>
        <div className="text-sm text-muted-foreground">{label}</div>
        <div className="font-display text-2xl font-bold mt-1 data-num">{value}</div>
      </div>
      <div className={`grid h-11 w-11 place-items-center rounded-xl bg-${tone}/10 text-${tone}`}>
        <Icon className="h-5 w-5" />
      </div>
    </div>
  </Card>
);

const quick = [
  { to: "/purchase", label: "New Purchase", icon: ShoppingCart },
  { to: "/payment", label: "Make Payment", icon: Wallet },
  { to: "/suppliers", label: "Add Supplier", icon: Users },
  { to: "/expenses", label: "Add Expense", icon: Receipt },
];

export default function Dashboard() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1); // 1-12
  const [year, setYear] = useState(now.getFullYear());
  const monthStr = `${year}-${String(month).padStart(2, "0")}`;
  const years = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 4 + i);

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", monthStr],
    queryFn: async () => (await api.get("/analysis/dashboard", { params: { month: monthStr } })).data,
  });

  const d = data || {};
  const trend = d.trend || [];

  return (
    <div>
      <PageTitle title="Dashboard" subtitle={`Store accounts for ${MONTHS[month - 1]} ${year}`}>
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="w-40" data-testid="dashboard-month"><SelectValue /></SelectTrigger>
          <SelectContent>
            {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="w-28" data-testid="dashboard-year"><SelectValue /></SelectTrigger>
          <SelectContent>
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </PageTitle>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={ShoppingCart} label="Month Purchases" value={inrShort(d.total_purchase)} testid="stat-purchase" />
        <Stat icon={Scale} label="Outstanding Balance" value={inrShort(d.total_balance)} tone="destructive" testid="stat-balance" />
        <Stat icon={Wallet} label="Paid on Bills" value={inrShort(d.total_paid)} tone="success" testid="stat-paid" />
        <Stat icon={Receipt} label="Month Expenses" value={inrShort(d.total_expense)} tone="accent" testid="stat-expense" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <Stat icon={Users} label="Suppliers" value={d.suppliers ?? 0} testid="stat-suppliers" />
        <Stat icon={UserCog} label="Employees" value={d.employees ?? 0} testid="stat-employees" />
        <Stat icon={ShoppingCart} label="Purchase Bills" value={d.purchase_count ?? 0} testid="stat-bills" />
        <Stat icon={Wallet} label="Payments Made" value={inrShort(d.total_payments)} tone="success" testid="stat-payments" />
      </div>

      <Link to="/purchase" data-testid="overdue-card" className="block">
        <Card className="p-5 mt-4 card-elevate border-border/70 flex items-center justify-between gap-4 transition-colors hover:bg-primary/5 border-primary/30">
          <div className="flex items-center gap-4">
            <div className="grid h-11 w-11 place-items-center rounded-xl bg-primary/10 text-primary">
              <ShoppingCart className="h-5 w-5" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Total Purchases — {MONTHS[month - 1]} {year} (before payments)</div>
              <div className="font-display text-2xl font-bold data-num text-primary" data-testid="overdue-count">{inr(d.total_purchase)}</div>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-muted-foreground" />
        </Card>
      </Link>

      <div className="grid lg:grid-cols-3 gap-4 mt-6">
        <Card className="lg:col-span-2 p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-primary" />
            <h3 className="font-display font-bold">Purchase vs Payment (last 6 months)</h3>
          </div>
          <div className="h-72">
            {trend.length === 0 ? (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">
                {isLoading ? "Loading…" : "No data yet — add purchases to see trends."}
              </div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trend} barGap={6}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(190 16% 90%)" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => inrShort(v)} tick={{ fontSize: 11 }} width={70} />
                  <Tooltip formatter={(v) => inr(v)} />
                  <Bar dataKey="purchase" name="Purchase" fill="hsl(176 62% 30%)" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="payment" name="Payment" fill="hsl(38 92% 50%)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-bold mb-4">Quick Actions</h3>
          <div className="space-y-2">
            {quick.map((q) => (
              <Link
                key={q.to}
                to={q.to}
                data-testid={`quick-${q.to.slice(1)}`}
                className="flex items-center justify-between rounded-lg border border-border px-4 py-3 hover:border-primary hover:bg-primary/5 transition-colors group"
              >
                <span className="flex items-center gap-3 text-sm font-medium">
                  <q.icon className="h-4 w-4 text-primary" /> {q.label}
                </span>
                <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
